import React, { useEffect } from "react";
import { withRouter } from "react-router-dom";
import { useQuery } from "@apollo/client";
import {
  Icon,
  Message,
  Header,
  Loader,
  List,
  Segment,
  Comment,
} from "semantic-ui-react";
import { GET_STUDENT_REPORT_RANGE } from "../GraphQL";
import useQueryString from "../hooks/useQueryString";
import { getReportTitle } from "../utils";
import Attachment from "components/Attachment";

const ReportView = ({ report, student, showComments }) => {
  const Items = report.fields.map((f, i) => {
    const itemProps = {
      header: f.label,
      description:
        f.control === "Checkbox" && f.value
          ? JSON.parse(f.value).join(", ")
          : f.value,
    };

    return itemProps.description ? (
      <List.Item key={`item${i}`}>
        <List.Header>{itemProps.header}</List.Header>
        <List.Description>{itemProps.description}</List.Description>
      </List.Item>
    ) : null;
  });

  return (
    <Segment.Group raised>
      <Message attached>
        <Message.Header>{student.name}</Message.Header>
        {getReportTitle(report)}
      </Message>
      <Segment attached>
        <List divided relaxed>
          {Items}
        </List>
      </Segment>
      {report.files && report.files.length > 0 ? (
        <Segment attached>
          <List divided relaxed>
            <List.Item>
              <List.Header>
                <Icon name="attach" />
                Attachments
              </List.Header>
              <List>
                {report.files.map((file) => (
                  <Attachment key={file.key} file={file} />
                ))}
              </List>
            </List.Item>
          </List>
        </Segment>
      ) : null}
      {report.comments && report.comments.length > 0 && showComments ? (
        <Segment attached>
          <Header as="h4">Report Comments</Header>
          <Comment.Group>
            {report.comments.map((comment, i) => (
              <Comment key={`comment${i}`}>
                <Comment.Content>
                  <Comment.Author as="a">
                    {comment.commentUser.name}
                  </Comment.Author>{" "}
                  <Comment.Metadata>
                    <div>{new Date(comment.date).toLocaleString()}</div>
                  </Comment.Metadata>
                  <Comment.Text>{comment.text}</Comment.Text>
                </Comment.Content>
              </Comment>
            ))}
          </Comment.Group>
        </Segment>
      ) : null}
    </Segment.Group>
  );
};

const ReportsViewPage = ({ match }) => {
  const { id } = match.params;
  const query = useQueryString();
  const { error, loading, data, fetchMore } = useQuery(
    GET_STUDENT_REPORT_RANGE,
    {
      variables: {
        studentId: id,
        ascending: true,
        startDate:
          query.get("startDate") || new Date(0).toISOString().slice(0, 10),
        endDate: query.get("endDate") || new Date().toISOString().slice(0, 10),
      },
    }
  );

  useEffect(() => {
    if (data) {
      if (data.getStudent.reports.nextToken) {
        console.log("fetching more");
        fetchMore({
          query: GET_STUDENT_REPORT_RANGE,
          variables: {
            studentId: id,
            startDate:
              query.get("startDate") || new Date(0).toISOString().slice(0, 10),
            endDate:
              query.get("endDate") || new Date().toISOString().slice(0, 10),
            ascending: true,
            nextToken: student.reports.nextToken,
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            console.log(previousResult);
            console.log(fetchMoreResult);
            const newReportConnection = fetchMoreResult.getStudent.reports;
            return {
              getStudent: Object.assign({}, previousResult.getStudent, {
                reports: Object.assign({}, previousResult.getStudent.reports, {
                  reports: [
                    ...previousResult.getStudent.reports.reports,
                    ...newReportConnection.reports,
                  ],
                  nextToken: newReportConnection.nextToken,
                }),
              }),
            };
          },
        });
      } else {
        console.log("fetching complete. dispatching event");
        window.parent.postMessage("reportsReady");
      }
    }
    // eslint-disable-next-line
  }, [data]);

  if (loading) {
    return <Loader active />;
  }
  if (error) {
    throw error;
  }

  const student = data.getStudent;

  const showComments = JSON.parse(query.get("showComments"));
  const showUnsentReports = JSON.parse(query.get("showUnsentReports"));

  if (!student.reports) {
    return (
      <Message negative>
        <Message.Header>No reports found.</Message.Header>
      </Message>
    );
  }

  return (
    <div>
      {student.reports.reports
        .filter((r) => showUnsentReports || r.sent)
        .map((report) => (
          <div className="paged" key={report.id}>
            <ReportView
              report={report}
              student={student}
              showComments={showComments}
            />
          </div>
        ))}
    </div>
  );
};

export default withRouter(ReportsViewPage);
